import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import type { ICellRendererAngularComp } from 'ag-grid-angular';
import type { ICellRendererParams } from 'ag-grid-community';

export interface SaveButtonContext {
  isDirty: (userId: string) => boolean;
  onSave: (userId: string) => void;
  getSavingUserId: () => string | null;
}

@Component({
  selector: 'app-save-button-cell-renderer',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (userId) {
      <button
        type="button"
        [disabled]="!isDirty || saving"
        (click)="onSaveClick()"
        class="rounded-md bg-slate-800 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {{ saving ? 'Saving...' : 'Save' }}
      </button>
    }
  `,
})
export class SaveButtonCellRendererComponent implements ICellRendererAngularComp {
  userId: string | null = null;
  isDirty = false;
  saving = false;
  private context: SaveButtonContext | null = null;
  private readonly cdr = inject(ChangeDetectorRef);

  agInit(params: ICellRendererParams): void {
    this.userId = params.data?.userId ?? null;
    this.context = params.context as SaveButtonContext;
    this.updateState();
  }

  refresh(params: ICellRendererParams): boolean {
    this.userId = params.data?.userId ?? null;
    this.context = params.context as SaveButtonContext;
    this.updateState();
    return true;
  }

  private updateState(): void {
    if (this.context && this.userId) {
      this.isDirty = this.context.isDirty(this.userId);
      const savingId = this.context.getSavingUserId?.() ?? null;
      this.saving = savingId === this.userId;
    } else {
      this.isDirty = false;
      this.saving = false;
    }
    this.cdr.markForCheck();
  }

  onSaveClick(): void {
    if (this.userId && this.context && this.isDirty && !this.saving) {
      this.context.onSave(this.userId);
    }
  }
}
